import {createModel} from '@rematch/core';

import {
  TVerificationWebsite,
  getUsersWebsites,
  getWebsiteDnsTextRecords,
  verifyWebsiteByDns,
  TDNSResponse,
  getWebsiteApiKey,
  regenerateApiKey,
  unsassignWebsite,
  verifyWebsiteByEmail,
  getProfilesBySiteId,
  reopenWebsite,
  updateSite,
  getImportHistory,
  HistoryItem,
  getImportHistoryLogs,
} from '~/services/hot-api/import-api';

import type {RootModel} from '.';

export interface ImportApiState {
  websites: {
    list: TVerificationWebsite[];
    loading: boolean;
    fail: boolean;
    done: boolean;
  };
  apiKey: {
    loading: boolean;
    key: string | null;
  };
  dnsRecords: {
    list: string[];
    done: boolean;
  };
  profiles: {
    siteId: number;
    loading: boolean;
    total: number;
    list: any[];
  };
  history: {
    total: number;
    list: HistoryItem[];
  };
  historyLogs: {
    total: number;
    list: any[];
    entity: HistoryItem | null;
  };
}

const defaultState: ImportApiState = {
  websites: {
    list: [],
    loading: true,
    fail: false,
    done: false,
  },
  apiKey: {
    loading: true,
    key: null,
  },
  dnsRecords: {
    list: [],
    done: false,
  },
  profiles: {
    siteId: -1,
    loading: true,
    total: 0,
    list: [],
  },
  history: {
    total: 0,
    list: [],
  },
  historyLogs: {
    total: 0,
    list: [],
    entity: null,
  },
};

export const importApi = createModel<RootModel>()({
  state: defaultState,
  reducers: {
    setPayload: (state: ImportApiState, payload: Partial<ImportApiState>) => ({
      ...state,
      ...payload,
    }),
    setWebsitesLoading: (state: ImportApiState) => ({
      ...state,
      websites: {
        ...state.websites,
        loading: true,
        done: false,
      },
    }),
    setWebsitesError: (state: ImportApiState) => ({
      ...state,
      websites: {
        ...state.websites,
        loading: false,
        fail: true,
        done: true,
      },
    }),
    setWebsites: (state: ImportApiState, websites: TVerificationWebsite[]) => ({
      ...state,
      websites: {
        list: websites,
        fail: false,
        loading: false,
        done: true,
      },
    }),
    updateWebsiteById: (state, website: TVerificationWebsite) => ({
      ...state,
      websites: {
        ...state.websites,
        list: state.websites.list.map((item) => (item.id === website.id ? website : item)),
      },
    }),
    setDNSRecordsLoading: (state: ImportApiState) => ({
      ...state,
      dnsRecords: {
        list: [],
        done: false,
      },
    }),
    setDNSRecords: (state: ImportApiState, records: string[]) => ({
      ...state,
      dnsRecords: {
        list: records,
        done: true,
      },
    }),
    setApiKeyLoading: (state: ImportApiState) => ({
      ...state,
      apiKey: {
        loading: true,
        key: null,
      },
    }),
    setApiKey: (state: ImportApiState, key: string | null) => ({
      ...state,
      apiKey: {
        loading: false,
        key,
      },
    }),
  },
  effects: (dispatch) => {
    return {
      async loadImportedWebsites(): Promise<void> {
        dispatch.importApi.setWebsitesLoading();

        try {
          const response = await getUsersWebsites();

          dispatch.importApi.setWebsites(response.data.items);
        } catch (e) {
          dispatch.importApi.setWebsitesError();
          console.error(e);
        }
      },
      async loadImportedWebsitesIfNeeded(_void: void, state): Promise<TVerificationWebsite[]> {
        if (state.importApi.websites.done) {
          return state.importApi.websites.list;
        }
        dispatch.importApi.setWebsitesLoading();

        try {
          const response = await getUsersWebsites();

          dispatch.importApi.setWebsites(response.data.items);
          return response.data.items;
        } catch (e) {
          dispatch.importApi.setWebsitesError();
          console.error(e);
          return [];
        }
      },
      async getSiteApiKey(siteId: number): Promise<string> {
        dispatch.importApi.setApiKeyLoading();
        try {
          const response = await getWebsiteApiKey(siteId);

          dispatch.importApi.setApiKey(response.data.key);
          return response.data.key;
        } catch (e) {
          console.error(e);
          return '';
        }
      },
      async regenerateApiKey(siteId: number): Promise<string> {
        dispatch.importApi.setApiKeyLoading();
        try {
          const response = await regenerateApiKey(siteId);

          dispatch.importApi.setApiKey(response.data.key);
          return response.data.key;
        } catch (e) {
          console.error(e);
          return '';
        }
      },
      async resendEmail(id: number): Promise<void> {
        try {
          await verifyWebsiteByEmail(id);
        } catch (e) {
          console.error(e);
        }

        return Promise.resolve();
      },
      async verifyByDns({id}): Promise<(TVerificationWebsite & {error: false}) | {error: true}> {
        try {
          const response = await verifyWebsiteByDns(id);

          return {...response.data, error: false};
        } catch (e) {
          console.error(e);
          return {error: true};
        }
      },
      async getWebsiteDnsTextRecords(id: number): Promise<TDNSResponse> {
        dispatch.importApi.setDNSRecordsLoading();
        try {
          const response = await getWebsiteDnsTextRecords(id);

          dispatch.importApi.setDNSRecords(response.data.records);
          return response.data;
        } catch (e) {
          console.error(e);
          return {records: []};
        }
      },
      async unsassignWebsite(id: number, state): Promise<void> {
        try {
          const response = await unsassignWebsite(id);
          const newWebsites = state.importApi.websites.list.filter((website) => website.id !== id);

          dispatch.importApi.setWebsites(newWebsites);
          return response.data;
        } catch (e) {
          console.error(e);
        }
      },
      async getProfiles(id: number, state): Promise<void> {
        if (state.importApi.profiles.siteId === id) {
          dispatch.importApi.setPayload({
            profiles: {
              ...state.importApi.profiles,
              loading: false,
            },
          });
          return;
        }

        try {
          dispatch.importApi.setPayload({
            profiles: {
              ...state.importApi.profiles,
              siteId: id,
              loading: true,
            },
          });

          const response = await getProfilesBySiteId(id);

          dispatch.importApi.setPayload({
            profiles: {
              loading: false,
              siteId: id,
              list: response.data.items,
              total: response.data.total,
            },
          });
          return response.data;
        } catch (e) {
          console.error(e);
        }
      },
      async getMoreProfiles(id: number, state): Promise<void> {
        if (state.importApi.profiles.siteId !== id) {
          return dispatch.importApi.getProfiles(id);
        }

        try {
          const loaded = state.importApi.profiles.list.length;
          const {total} = state.importApi.profiles;

          if (total <= loaded) {
            return;
          }

          dispatch.importApi.setPayload({
            profiles: {
              ...state.importApi.profiles,
              loading: true,
            },
          });

          const response = await getProfilesBySiteId(id, loaded);

          dispatch.importApi.setPayload({
            profiles: {
              ...state.importApi.profiles,
              loading: false,
              list: [...state.importApi.profiles.list, ...response.data.items],
            },
          });
          return response.data;
        } catch (e) {
          console.error(e);
        }
      },
      async reopenWebsite(id: number): Promise<void> {
        try {
          await reopenWebsite(id);
          await dispatch.importApi.loadImportedWebsites();
        } catch (e) {
          console.error(e);
        }
      },
      async loadImportHistory({siteId}: any) {
        dispatch.importApi.setPayload({
          history: {
            total: 0,
            list: [],
          },
        });

        try {
          const response = await getImportHistory({siteId});

          dispatch.importApi.setPayload({
            history: {
              total: response.data.total,
              list: response.data.items,
            },
          });
          return response.data;
        } catch (e) {
          console.error(e);

          dispatch.importApi.setPayload({
            history: {
              total: 0,
              list: [],
            },
          });
        }
      },
      async getMoreHistory({siteId}: any, state): Promise<any> {
        try {
          const loaded = state.importApi.history.list.length;
          const {total} = state.importApi.history;

          if (total <= loaded) {
            return;
          }

          const response = await getImportHistory({siteId, offset: loaded});

          dispatch.importApi.setPayload({
            history: {
              ...state.importApi.history,
              list: [...state.importApi.history.list, ...response.data.items],
            },
          });

          return response.data;
        } catch (e) {
          console.error(e);
        }
      },
      async changeWebsiteMode({siteId, verificationId, showOnlyImported}: any, state) {
        try {
          await updateSite(siteId, {show_only_imported: showOnlyImported});
        } catch (e) {
          console.error(e);
        }

        dispatch.importApi.setPayload({
          websites: {
            ...state.importApi.websites,
            list: state.importApi.websites.list.map((website) => {
              if (website.id === verificationId) {
                return {
                  ...website,
                  site: {
                    ...website.site,
                    show_only_imported: showOnlyImported,
                  },
                };
              }
              return website;
            }),
          },
        });
      },
      async loadHistoryLogs({siteId, importHistoryId}: any) {
        dispatch.importApi.setPayload({
          historyLogs: {
            total: 0,
            list: [],
            entity: null,
          },
        });

        try {
          const response = await getImportHistoryLogs({siteId, importHistoryId});

          dispatch.importApi.setPayload({
            historyLogs: {
              total: response.data.total,
              list: response.data.items,
              entity: response.data.entity,
            },
          });
          return response.data;
        } catch (e) {
          console.error(e);

          dispatch.importApi.setPayload({
            historyLogs: {
              total: 0,
              list: [],
              entity: null,
            },
          });
        }
      },
      async loadMoreHistoryLogs({siteId, importHistoryId}: any, state) {
        try {
          const loaded = state.importApi.historyLogs.list.length;
          const {total} = state.importApi.historyLogs;

          if (total <= loaded) {
            return;
          }

          const response = await getImportHistoryLogs({siteId, importHistoryId, offset: loaded});

          dispatch.importApi.setPayload({
            historyLogs: {
              ...state.importApi.historyLogs,
              list: [...state.importApi.historyLogs.list, ...response.data.items],
            },
          });

          return response.data;
        } catch (e) {
          console.error(e);
        }
      },
    };
  },
});
